import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import axios from 'axios';
import { toast } from 'sonner';
import { FileText, Download, CheckCircle, Clock, Calendar, ChevronLeft, ChevronRight, FileDown, StickyNote, AlertCircle, Eye } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export const BackshopExport = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [exports, setExports] = useState([]);
  const [locations, setLocations] = useState([]); // NEW: Locations
  const [calendarDays, setCalendarDays] = useState([]);
  
  // Filter State
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedLocationId, setSelectedLocationId] = useState(''); // NEW: Selected location
  const [exportType, setExportType] = useState('both'); // 'backshop', 'tgtg', 'both'
  const [exportFormat, setExportFormat] = useState('summary'); // 'detailed', 'summary'
  const [includeBarcode, setIncludeBarcode] = useState(true); // Include barcode in PDF (default: yes)
  
  // Calendar State
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedExport, setSelectedExport] = useState(null);
  
  // Complete Dialog
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const [completingExport, setCompletingExport] = useState(null);
  const [completeNote, setCompleteNote] = useState('');

  useEffect(() => {
    fetchLocations(); // NEW: Fetch locations
    fetchExports();
    
    // Set default dates (current month)
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    
    setStartDate(firstDay.toISOString().split('T')[0]);
    setEndDate(lastDay.toISOString().split('T')[0]);
  }, []);

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/locations`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const locs = response.data || [];
      setLocations(locs);
      
      // Auto-select first location if only one exists
      if (locs.length === 1) {
        setSelectedLocationId(locs[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch locations:', error);
      toast.error('Fehler beim Laden der Standorte');
    }
  };

  useEffect(() => {
    generateCalendar();
  }, [currentMonth, exports]);

  const fetchExports = async () => {
    try {
      const response = await axios.get(`${API}/api/backshop/exports`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setExports(response.data || []);
    } catch (error) {
      console.error('Failed to fetch exports:', error);
      if (error.response?.status !== 401) {
        toast.error('Fehler beim Laden der Export-Historie');
      }
      setExports([]);
    } finally {
      setLoading(false);
    }
  };

  const generateCalendar = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDayOfWeek = firstDay.getDay() || 7; // Monday = 1
    
    const days = [];
    
    // Previous month padding
    for (let i = 1; i < startDayOfWeek; i++) {
      days.push({ date: null, status: null });
    }
    
    // Current month days
    for (let day = 1; day <= lastDay.getDate(); day++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const status = getDayStatus(dateStr);
      days.push({ date: dateStr, day, status });
    }
    
    setCalendarDays(days);
  };

  const getDayStatus = (dateStr) => {
    // Check if this day is part of any export
    const dayExports = exports.filter(exp => {
      const start = new Date(exp.start_date);
      const end = new Date(exp.end_date);
      const current = new Date(dateStr);
      return current >= start && current <= end;
    });
    
    if (dayExports.length === 0) return 'none'; // ⚪ Not exported
    
    const hasCompleted = dayExports.some(exp => exp.status === 'completed');
    const hasPending = dayExports.some(exp => exp.status === 'pending');
    
    if (hasCompleted) return 'completed'; // 🟢 In WWS übertragen
    if (hasPending) return 'pending'; // 🟡 Exportiert, aber noch nicht übertragen
    
    return 'none';
  };

  const handleExport = async () => {
    if (!startDate || !endDate) {
      toast.error('Bitte wählen Sie einen Zeitraum');
      return;
    }
    
    if (!selectedLocationId) {
      toast.error('Bitte wählen Sie einen Standort');
      return;
    }
    
    if (new Date(startDate) > new Date(endDate)) {
      toast.error('Start-Datum muss vor End-Datum liegen');
      return;
    }
    
    // Check for overlaps with completed exports
    const overlaps = checkOverlaps(startDate, endDate);
    if (overlaps.length > 0) {
      const overlapDates = overlaps.map(o => new Date(o.start_date).toLocaleDateString('de-DE')).join(', ');
      
      const confirmed = window.confirm(
        `⚠️ Achtung: Überschneidung erkannt!\n\n` +
        `Folgende Zeiträume wurden bereits in WWS übertragen:\n${overlapDates}\n\n` +
        `Möchten Sie trotzdem fortfahren?\n` +
        `Dies könnte zu doppelten Einträgen in Ihrem WWS führen!`
      );
      
      if (!confirmed) return;
    }
    
    setExporting(true);
    
    try {
      const response = await axios.post(
        `${API}/api/backshop/export`,
        {
          start_date: startDate,
          end_date: endDate,
          location_id: selectedLocationId, // NEW: Include location
          type: exportType,
          format: exportFormat,
          include_barcode: includeBarcode
        },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      const exportData = response.data;
      
      toast.success(`Export erstellt! ${exportData.total_quantity} Stück aus ${exportData.total_products} Produkten`);
      
      // Reload exports
      await fetchExports();
      
      // Scroll to export history
      setTimeout(() => {
        const historyElement = document.querySelector('[data-export-history]');
        if (historyElement) {
          historyElement.scrollIntoView({ behavior: 'smooth' });
        }
      }, 500);
      
    } catch (error) {
      console.error('Export failed:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unbekannter Fehler';
      toast.error(`Fehler beim Erstellen des Exports: ${errorMsg}`);
    } finally {
      setExporting(false);
    }
  };

  const checkOverlaps = (start, end) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    
    return exports.filter(exp => {
      if (exp.status !== 'completed') return false;
      
      const expStart = new Date(exp.start_date);
      const expEnd = new Date(exp.end_date);
      
      // Check if ranges overlap
      return (startDate <= expEnd && endDate >= expStart);
    });
  };

  const openCompleteDialog = (exportItem) => {
    setCompletingExport(exportItem);
    setCompleteNote(exportItem.note || '');
    setShowCompleteDialog(true);
  };

  const handleComplete = async () => {
    if (!completingExport) return;
    
    try {
      await axios.put(
        `${API}/api/backshop/exports/${completingExport.id}/complete`,
        { note: completeNote },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      toast.success('✅ Export als erledigt markiert!');
      setShowCompleteDialog(false);
      setCompletingExport(null);
      setCompleteNote('');
      await fetchExports();
    } catch (error) {
      console.error('Failed to complete export:', error);
      toast.error('Fehler beim Markieren');
    }
  };

  const updateNote = async (exportId, note) => {
    try {
      await axios.put(
        `${API}/api/backshop/exports/${exportId}/note`,
        { note },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );
      toast.success('Notiz aktualisiert');
      await fetchExports();
    } catch (error) {
      console.error('Failed to update note:', error);
      toast.error('Fehler beim Speichern der Notiz');
    }
  };

  const downloadPDF = async (exportId) => {
    try {
      toast.info('PDF wird vorbereitet...');
      
      const response = await axios.get(
        `${API}/api/backshop/exports/${exportId}/pdf`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          responseType: 'blob',
          timeout: 30000 // 30 second timeout
        }
      );
      
      // Verify we got a PDF
      if (!response.data || response.data.size === 0) {
        throw new Error('PDF ist leer');
      }
      
      // Create blob URL and download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Export-${exportId}.pdf`;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      
      toast.success('PDF erfolgreich heruntergeladen!');
    } catch (error) {
      console.error('PDF download failed:', error);
      
      let errorMsg = 'Fehler beim Herunterladen des PDFs';
      if (error.response) {
        if (error.response.status === 404) {
          errorMsg = 'PDF nicht gefunden';
        } else if (error.response.status === 401) {
          errorMsg = 'Nicht autorisiert - bitte neu anmelden';
        }
      } else if (error.message) {
        errorMsg = `Fehler: ${error.message}`;
      }
      
      toast.error(errorMsg);
    }
  };

  const getStatusBadge = (status) => {
    if (status === 'completed') {
      return (
        <Badge className="bg-green-100 text-green-700 border-green-300">
          <CheckCircle className="h-3 w-3 mr-1" />
          In WWS übertragen
        </Badge>
      );
    }
    return (
      <Badge variant="secondary" className="bg-yellow-100 text-yellow-700 border-yellow-300">
        <Clock className="h-3 w-3 mr-1" />
        Noch nicht übertragen
      </Badge>
    );
  };

  const getTypeName = (type) => {
    if (type === 'backshop') return 'Nur Backshop';
    if (type === 'tgtg') return 'Nur TGTG';
    return 'Backshop + TGTG';
  };

  const getFormatName = (format) => {
    return format === 'detailed' ? 'Detailliert' : 'Zusammenfassung';
  };

  const monthNames = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 
                     'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin h-8 w-8 border-4 border-orange-500 border-t-transparent rounded-full mx-auto"></div>
            <p className="mt-4 text-gray-600 dark:text-gray-400">Laden...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 pb-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold" style={{fontFamily: 'Manrope, sans-serif'}}>
            Abschrift-Export
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground mt-2">
            Exportieren Sie Ihre Abschriften für Ihr Warenwirtschaftssystem
          </p>
        </div>

        {/* Export Form */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileDown className="h-5 w-5 text-orange-600" />
              Neuer Export erstellen
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Date Range */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Von *</Label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Bis *</Label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            </div>

            {/* NEW: Location Selection */}
            <div className="space-y-2">
              <Label>Standort *</Label>
              <Select value={selectedLocationId} onValueChange={setSelectedLocationId}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Standort wählen..." />
                </SelectTrigger>
                <SelectContent>
                  {locations.map(location => (
                    <SelectItem key={location.id} value={location.id}>
                      📍 {location.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {locations.length === 0 && (
                <p className="text-xs text-gray-500">Keine Standorte verfügbar</p>
              )}
            </div>

            {/* Type Selection */}
            <div className="space-y-2">
              <Label>Typ *</Label>
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  onClick={() => setExportType('backshop')}
                  className={`flex-1 px-4 py-3 rounded-lg border-2 text-sm font-medium transition-all ${
                    exportType === 'backshop'
                      ? 'bg-orange-500 text-white border-orange-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:border-orange-400'
                  }`}
                >
                  🏪 Nur Backshop
                </button>
                <button
                  onClick={() => setExportType('tgtg')}
                  className={`flex-1 px-4 py-3 rounded-lg border-2 text-sm font-medium transition-all ${
                    exportType === 'tgtg'
                      ? 'bg-green-500 text-white border-green-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:border-green-400'
                  }`}
                >
                  🌱 Nur TGTG
                </button>
                <button
                  onClick={() => setExportType('both')}
                  className={`flex-1 px-4 py-3 rounded-lg border-2 text-sm font-medium transition-all ${
                    exportType === 'both'
                      ? 'bg-blue-500 text-white border-blue-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
                  }`}
                >
                  📦 Beide
                </button>
              </div>
            </div>

            {/* Format Selection */}
            <div className="space-y-2">
              <Label>Format *</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button
                  onClick={() => setExportFormat('detailed')}
                  className={`p-4 rounded-lg border-2 text-left transition-all ${
                    exportFormat === 'detailed'
                      ? 'bg-blue-50 border-blue-500'
                      : 'bg-white border-gray-300 hover:border-blue-400'
                  }`}
                >
                  <div className="font-medium text-sm mb-1">📋 Detailliert (Tag für Tag)</div>
                  <div className="text-xs text-gray-600 dark:text-gray-400">
                    Zeigt jede Abschrift mit Datum, Mitarbeiter, Produkt und Menge
                  </div>
                </button>
                <button
                  onClick={() => setExportFormat('summary')}
                  className={`p-4 rounded-lg border-2 text-left transition-all ${
                    exportFormat === 'summary'
                      ? 'bg-blue-50 border-blue-500'
                      : 'bg-white border-gray-300 hover:border-blue-400'
                  }`}
                >
                  <div className="font-medium text-sm mb-1">📊 Zusammenfassung (Aggregiert)</div>
                  <div className="text-xs text-gray-600 dark:text-gray-400">
                    Gesamtsummen pro Produkt für schnelles WWS-Abschreiben
                  </div>
                </button>
              </div>
            </div>

            {/* Barcode Toggle */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Barcode im PDF</Label>
              <div className="flex items-center justify-between p-4 border-2 rounded-lg transition-all" 
                   style={{
                     borderColor: includeBarcode ? '#22c55e' : '#d1d5db',
                     backgroundColor: includeBarcode ? '#f0fdf4' : '#f9fafb'
                   }}>
                <div className="flex items-center gap-3 flex-1">
                  <div className={`text-2xl ${includeBarcode ? '' : 'grayscale opacity-50'}`}>
                    📊
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-sm">
                      {includeBarcode ? '✓ Mit Barcode' : 'Ohne Barcode'}
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                      {includeBarcode 
                        ? 'Barcodes werden im PDF angezeigt (scanbar)' 
                        : 'Nur Artikelnummern, keine Barcodes'}
                    </div>
                  </div>
                </div>
                <Switch
                  checked={includeBarcode}
                  onCheckedChange={setIncludeBarcode}
                  className="data-[state=checked]:bg-green-500"
                />
              </div>
              <p className="text-xs text-gray-500 mt-2">
                💡 Hinweis: Die Web-Ansicht zeigt immer Barcodes an. Diese Einstellung betrifft nur das PDF.
              </p>
            </div>

            {/* Export Button */}
            <Button
              onClick={handleExport}
              disabled={exporting || !startDate || !endDate || !selectedLocationId}
              className="w-full h-12 bg-orange-600 hover:bg-orange-700 text-base font-semibold"
            >
              {exporting ? (
                <>
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                  Export wird erstellt...
                </>
              ) : (
                <>
                  <FileDown className="h-5 w-5 mr-2" />
                  PDF Export erstellen
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Visual Calendar */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-orange-600" />
              Kalender-Übersicht
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Month Navigation */}
            <div className="flex items-center justify-between mb-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-base sm:text-lg font-semibold">
                {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-1 sm:gap-2">
              {/* Day Headers */}
              {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map(day => (
                <div key={day} className="text-center text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 py-2">
                  {day}
                </div>
              ))}
              
              {/* Calendar Days */}
              {calendarDays.map((dayObj, idx) => {
                if (!dayObj.date) {
                  return <div key={idx} className="aspect-square"></div>;
                }
                
                let bgColor = 'bg-white';
                let emoji = '';
                
                if (dayObj.status === 'completed') {
                  bgColor = 'bg-green-100 border-green-400';
                  emoji = '🟢';
                } else if (dayObj.status === 'pending') {
                  bgColor = 'bg-yellow-100 border-yellow-400';
                  emoji = '🟡';
                } else {
                  bgColor = 'bg-gray-50 border-gray-200';
                  emoji = '⚪';
                }
                
                return (
                  <button
                    key={idx}
                    className={`aspect-square rounded-lg border-2 flex flex-col items-center justify-center text-xs sm:text-sm hover:shadow-md transition-all ${bgColor}`}
                    onClick={() => {
                      const dayExports = exports.filter(exp => {
                        const start = new Date(exp.start_date);
                        const end = new Date(exp.end_date);
                        const current = new Date(dayObj.date);
                        return current >= start && current <= end;
                      });
                      if (dayExports.length > 0) {
                        setSelectedExport(dayExports[0]);
                      }
                    }}
                  >
                    <div className="text-xs sm:text-sm">{emoji}</div>
                    <div className="font-medium">{dayObj.day}</div>
                  </button>
                );
              })}
            </div>

            {/* Legend */}
            <div className="mt-4 flex flex-wrap gap-4 text-xs sm:text-sm">
              <div className="flex items-center gap-2">
                <span>🟢</span>
                <span className="text-gray-700 dark:text-gray-300">In WWS übertragen</span>
              </div>
              <div className="flex items-center gap-2">
                <span>🟡</span>
                <span className="text-gray-700 dark:text-gray-300">Noch nicht übertragen</span>
              </div>
              <div className="flex items-center gap-2">
                <span>⚪</span>
                <span className="text-gray-700 dark:text-gray-300">Nicht exportiert</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Export History */}
        <Card data-export-history>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-orange-600" />
              Export-Verlauf
            </CardTitle>
          </CardHeader>
          <CardContent>
            {exports.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <FileText className="h-12 w-12 mx-auto mb-3 text-gray-400" />
                <p>Noch keine Exporte erstellt</p>
              </div>
            ) : (
              <div className="space-y-4">
                {exports.map(exp => (
                  <div key={exp.id} className="border-2 rounded-lg p-4 hover:shadow-md transition-all">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-mono text-xs text-gray-500">#{exp.id}</span>
                          {getStatusBadge(exp.status)}
                        </div>
                        
                        <div className="space-y-1 text-sm">
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-gray-400" />
                            <span className="font-medium">
                              {new Date(exp.start_date).toLocaleDateString('de-DE')} - {new Date(exp.end_date).toLocaleDateString('de-DE')}
                            </span>
                          </div>
                          
                          <div className="flex flex-wrap gap-2 text-xs">
                            <Badge variant="outline">{getTypeName(exp.type)}</Badge>
                            <Badge variant="outline">{getFormatName(exp.format)}</Badge>
                            <Badge variant="outline">{exp.total_products} Produkte</Badge>
                            <Badge variant="outline">{exp.total_quantity} Stück</Badge>
                          </div>
                          
                          {/* Detailed breakdown for "both" type */}
                          {exp.type === 'both' && (exp.backshop_quantity > 0 || exp.tgtg_quantity > 0) && (
                            <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 space-y-2">
                              <div className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">Aufschlüsselung:</div>
                              
                              {exp.backshop_quantity > 0 && (
                                <div className="flex items-center justify-between text-xs">
                                  <span className="flex items-center gap-1.5">
                                    <span className="w-2 h-2 rounded-full bg-orange-500"></span>
                                    <span className="font-medium text-gray-700 dark:text-gray-300">Backshop</span>
                                  </span>
                                  <span className="font-semibold text-orange-700">{exp.backshop_quantity} Stück</span>
                                </div>
                              )}
                              
                              {exp.tgtg_quantity > 0 && (
                                <div className="flex items-center justify-between text-xs">
                                  <span className="flex items-center gap-1.5">
                                    <span className="w-2 h-2 rounded-full bg-green-500"></span>
                                    <span className="font-medium text-gray-700 dark:text-gray-300">TGTG</span>
                                  </span>
                                  <span className="font-semibold text-green-700">{exp.tgtg_quantity} Stück</span>
                                </div>
                              )}
                              
                              <div className="flex items-center justify-between text-xs pt-2 border-t border-gray-300 dark:border-gray-600">
                                <span className="flex items-center gap-1.5">
                                  <span className="w-2 h-2 rounded-full bg-blue-600"></span>
                                  <span className="font-bold text-gray-900 dark:text-gray-100">Gesamt</span>
                                </span>
                                <span className="font-bold text-blue-700">{exp.total_quantity} Stück</span>
                              </div>
                            </div>
                          )}
                          
                          {exp.note && (
                            <div className="flex items-start gap-2 mt-2 p-2 bg-blue-50 rounded text-xs">
                              <StickyNote className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
                              <span className="text-gray-700 dark:text-gray-300">{exp.note}</span>
                            </div>
                          )}
                          
                          {exp.completed_at && (
                            <div className="text-xs text-gray-500 mt-2">
                              ✓ Übertragen am: {new Date(exp.completed_at).toLocaleString('de-DE')}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex flex-col gap-2">
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => navigate(`/admin/abschriften/backshop-export/${exp.id}`)}
                          className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700"
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          Ansehen
                        </Button>
                        
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => downloadPDF(exp.id)}
                          className="w-full sm:w-auto"
                        >
                          <Download className="h-4 w-4 mr-1" />
                          PDF laden
                        </Button>
                        
                        {exp.status === 'pending' && (
                          <Button
                            size="sm"
                            onClick={() => openCompleteDialog(exp)}
                            className="w-full sm:w-auto bg-green-600 hover:bg-green-700"
                          >
                            <CheckCircle className="h-4 w-4 mr-1" />
                            Als erledigt
                          </Button>
                        )}
                        
                        {exp.status === 'completed' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const newNote = prompt('Notiz bearbeiten:', exp.note || '');
                              if (newNote !== null) {
                                updateNote(exp.id, newNote);
                              }
                            }}
                            className="w-full sm:w-auto text-xs"
                          >
                            <StickyNote className="h-3 w-3 mr-1" />
                            Notiz
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Complete Dialog */}
        <AlertDialog open={showCompleteDialog} onOpenChange={setShowCompleteDialog}>
          <AlertDialogContent className="max-w-md">
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <CheckCircle className="h-6 w-6 text-green-600" />
                Als erledigt markieren
              </AlertDialogTitle>
              <AlertDialogDescription className="space-y-3 pt-3">
                {completingExport && (
                  <>
                    <p className="text-base text-gray-700 dark:text-gray-300">
                      Bitte bestätigen Sie, dass dieser Export in Ihr Warenwirtschaftssystem übertragen wurde.
                    </p>
                    
                    <div className="space-y-2 bg-gray-50 dark:bg-gray-800 p-3 rounded-lg text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Zeitraum:</span>
                        <span className="font-medium">
                          {new Date(completingExport.start_date).toLocaleDateString('de-DE')} - {new Date(completingExport.end_date).toLocaleDateString('de-DE')}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Typ:</span>
                        <span className="font-medium">{getTypeName(completingExport.type)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Gesamt:</span>
                        <span className="font-bold text-lg">{completingExport.total_quantity} Stück</span>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">
                        📝 Notiz (optional)
                      </Label>
                      <Input
                        placeholder="z.B. In SAP übertragen, Belegnummer: SAP-2025-001"
                        value={completeNote}
                        onChange={(e) => setCompleteNote(e.target.value)}
                        className="text-sm"
                      />
                      <p className="text-xs text-gray-500">
                        Hinterlegen Sie hier z.B. die Belegnummer aus Ihrem WWS
                      </p>
                    </div>
                  </>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="gap-2">
              <AlertDialogCancel>Abbrechen</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleComplete}
                className="bg-green-600 hover:bg-green-700"
              >
                ✓ Bestätigen & Erledigt
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
};
